import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Login.css";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import "font-awesome/css/font-awesome.min.css";
import GitHubLogin from "react-github-login";
import { jwtDecode } from "jwt-decode";
import { Helmet } from "react-helmet";

import { toast, Toaster } from "react-hot-toast";

const Login = () => {

  
<Helmet>
  <title>Login – V-Editor Online Code Editor</title>
  <meta
    name="description"
    content="Login to V-Editor to access your projects, live collaboration, and AI-powered coding suggestions in a multi-language online code editor."
  />
  <meta
    name="keywords"
    content="V-Editor login, online code editor login, Python editor login, JavaScript editor login, HTML CSS editor login"
  />
  <meta name="robots" content="index, follow" />
</Helmet>

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL;
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_IDS;
  const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID;

  useEffect(() => {
    setEmail("");
    setPassword("");
    
    // Check if already accepted terms
    const hasAccepted = localStorage.getItem("v-editor-terms-accepted");
    if (hasAccepted === "true") {
      setAcceptTerms(true);
      setAcceptPrivacy(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if terms are accepted
    if (!acceptTerms || !acceptPrivacy) {
      toast.error("Please accept Terms & Conditions and Privacy Policy");
      return;
    }
    
    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/register.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signIn: true,
          email,
          password,
        }),
      });

      const result = await res.json();
      setLoading(false);

      if (result.success) {
        localStorage.setItem("v-editor-terms-accepted", "true");
        toast.success("Login successful!");
        localStorage.setItem("token", result.token);
        navigate("/home", { state: result });
      } else {
        toast.error(result.message || "Login failed");
        setMessage(result.message);
      }
    } catch (err) {
      setLoading(false);
      toast.error("Network error: " + err.message);
      setMessage("Login failed");
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    if (!acceptTerms || !acceptPrivacy) {
      toast.error("Please accept Terms & Conditions and Privacy Policy first");
      return;
    }
    
    const decoded = jwtDecode(credentialResponse.credential);
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/register.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: "google",
          email: decoded.email,
          firstName: decoded.given_name,
          lastName: decoded.family_name || "",
          picture: decoded.picture,
        }),
      });

      const result = await res.json();

      if (result.success) {
        localStorage.setItem("v-editor-terms-accepted", "true");
        toast.success("Google login successful!");
        localStorage.setItem("token", result.token);
        navigate("/home", { state: result });
      } else {
        toast.error(result.message || "Google login failed");
      }
    } catch (err) {
      toast.error("Google login network error: " + err.message);
    }
  };

  const handleGitHubLoginSuccess = async (response) => {
    if (!acceptTerms || !acceptPrivacy) {
      toast.error("Please accept Terms & Conditions and Privacy Policy first");
      return;
    }
    
    if (!response.code) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/register.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          provider: "github",
          github_code: response.code,
        }),
      });

      const result = await res.json();

      if (result.success) {
        localStorage.setItem("v-editor-terms-accepted", "true");
        toast.success("GitHub login successful!");
        localStorage.setItem("token", result.token);
        navigate("/home", { state: result });
      } else {
        toast.error(result.message || "GitHub login failed");
      }
    } catch (err) {
      toast.error("GitHub login network error: " + err.message);
    }
  };

  const handleAcceptAll = () => {
    setAcceptTerms(true);
    setAcceptPrivacy(true);
    toast.success("All terms accepted!");
  };

  useEffect(() => {
    const handleFocus = (e) => e.target.classList.add("typing");

    const inputs = document.querySelectorAll("input");
    inputs.forEach((i) => i.addEventListener("focus", handleFocus));

    return () => {
      inputs.forEach((i) => i.removeEventListener("focus", handleFocus));
    };
  }, []);

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      {/* Add Toaster for notifications */}
      <Toaster position="top-center" />
      
      {/* Wrap everything in login-page div for proper styling */}
      <div className="login-page">
        <div className="login-container">
          <div className="login-card">
            <div className="login-header">
              <h1 className="login-title">Welcome Back</h1>
              <p className="login-subtitle">Sign in to your account</p>
            </div>
            
            {/* Terms Section */}
            <div className="terms-section">
              <div className="terms-header">
                <i className="fa fa-exclamation-circle"></i>
                Before logging in, please accept:
              </div>
              
              <div className="terms-checkboxes">
                <div className="term-item">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="termsCheck"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="termsCheck">
                      I agree to the{" "}
                      <button
                        className="link-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowTermsModal(true);
                        }}
                      >
                        Terms & Conditions
                      </button>
                    </label>
                  </div>
                </div>
                
                <div className="term-item">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="privacyCheck"
                      checked={acceptPrivacy}
                      onChange={(e) => setAcceptPrivacy(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="privacyCheck">
                      I agree to the{" "}
                      <button
                        className="link-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowPrivacyModal(true);
                        }}
                      >
                        Privacy Policy
                      </button>
                    </label>
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleAcceptAll}
                className="accept-all-btn"
                disabled={acceptTerms && acceptPrivacy}
              >
                <i className="fa fa-check-circle"></i>
                Accept All
              </button>
            </div>
            
            {/* Login Form */}
            <form className="login-form" onSubmit={handleSubmit} autoComplete="off">
              <div className="form-group">
                <label className="form-label">
                  <i className="fa fa-envelope"></i>
                  Email Address
                </label>
                <input
                  type="email"
                  className="form-control"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="off"
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <i className="fa fa-lock"></i>
                  Password
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                />
                <div className="password-options">
                  <a href="#" className="forgot-password">
                    Forgot Password?
                  </a>
                </div>
              </div>
              
              <button 
                type="submit" 
                className="login-btn"
                disabled={loading || !acceptTerms || !acceptPrivacy}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Signing in...
                  </>
                ) : "Sign In"}
              </button>
            </form>
            
            {/* Divider */}
            <div className="divider">
              <span>Or continue with</span>
            </div>
            
            {/* Social Logins */}
            <div className="social-logins">
              <GoogleLogin
                onSuccess={handleGoogleLoginSuccess}
                onError={() => {
                  toast.error("Google login failed");
                  setMessage("Google login failed");
                }}
                disabled={!acceptTerms || !acceptPrivacy}
                width="100%"
                size="large"
              />
              
              <GitHubLogin
                clientId={GITHUB_CLIENT_ID}
                onSuccess={handleGitHubLoginSuccess}
                onFailure={() => {
                  toast.error("GitHub login failed");
                  setMessage("GitHub login failed");
                }}
                redirectUri="http://veditor.in/auth/github/callback"
                className="github-btn"
                disabled={!acceptTerms || !acceptPrivacy}
                buttonText="Continue with GitHub"
              >
                <i className="fa fa-github me-2"></i>
                Continue with GitHub
              </GitHubLogin>
            </div>
            
            {/* Footer */}
            <div className="login-footer">
              <p className="no-account">
                Don't have an account?
                <button 
                  className="signup-link" 
                  onClick={() => navigate("/register")}
                >
                  Sign Up
                </button>
              </p>
              
              <div className="legal-note">
                <p>
                  By signing in, you confirm you've read and agree to our{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> and{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms Modal */}
      {showTermsModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h5>
                <i className="fa fa-file-text me-2"></i>
                Terms & Conditions
              </h5>
              <button 
                className="modal-close"
                onClick={() => setShowTermsModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="terms-content">
                <p><strong>By using V-Editor, you agree to:</strong></p>
                <ul>
                  <li>Use the service for lawful purposes only</li>
                  <li>Not share inappropriate content</li>
                  <li>Respect other users' privacy</li>
                  <li>Accept that this is a beta version</li>
                  <li>Report any bugs or issues</li>
                </ul>
                <p>Full terms available at <a href="/terms" target="_blank" rel="noopener noreferrer">/terms</a></p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn"
                onClick={() => setShowTermsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {showPrivacyModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h5>
                <i className="fa fa-shield-alt me-2"></i>
                Privacy Policy
              </h5>
              <button 
                className="modal-close"
                onClick={() => setShowPrivacyModal(false)}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="privacy-content">
                <p><strong>We collect and use your data to:</strong></p>
                <ul>
                  <li>Provide and improve our services</li>
                  <li>Secure your account</li>
                  <li>Send important updates</li>
                  <li>Analyze usage patterns</li>
                </ul>
                <p>We don't sell your data to third parties.</p>
                <p>Full policy available at <a href="/privacy" target="_blank" rel="noopener noreferrer">/privacy</a></p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-btn"
                onClick={() => setShowPrivacyModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Alert */}
      {message && (
        <div className="alert alert-info" role="alert">
          {message}
        </div>
      )}
    </GoogleOAuthProvider>
  );
};

export default Login;